import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, AlertTriangle } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function PolicyDialog({ open, onClose, policy, officers, issues, capabilities }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    statement: '',
    rationale: '',
    restrictions: '',
    enforcement_mechanism: '',
    relevance_scope: '',
    time_bound: '',
    linked_issue_ids: [],
    interim_for_capability_development: false,
    review_date: '',
    sunset_trigger: '',
    linked_capability_ids: [],
    enforceability_score: '',
    enforceability_notes: ''
  });

  useEffect(() => {
    if (policy) {
      setFormData({
        title: policy.title || '',
        statement: policy.statement || '',
        rationale: policy.rationale || '',
        restrictions: policy.restrictions || '',
        enforcement_mechanism: policy.enforcement_mechanism || '',
        relevance_scope: policy.relevance_scope || '',
        time_bound: policy.time_bound || '',
        linked_issue_ids: policy.linked_issue_ids || [],
        interim_for_capability_development: policy.interim_for_capability_development || false,
        review_date: policy.review_date || '',
        sunset_trigger: policy.sunset_trigger || '',
        linked_capability_ids: policy.linked_capability_ids || [],
        enforceability_score: policy.enforceability_score || '',
        enforceability_notes: policy.enforceability_notes || ''
      });
    } else {
      setFormData({
        title: '',
        statement: '',
        rationale: '',
        restrictions: '',
        enforcement_mechanism: '',
        relevance_scope: '',
        time_bound: '',
        linked_issue_ids: [],
        interim_for_capability_development: false,
        review_date: '',
        sunset_trigger: '',
        linked_capability_ids: [],
        enforceability_score: '',
        enforceability_notes: ''
      });
    }
  }, [policy, open]);

  const createPolicyMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.Policy.create({
        ...data,
        designed_by: user.email,
        designed_at: new Date().toISOString(),
        status: 'draft'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      onClose();
    }
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Policy.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      onClose();
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (policy && policy.id) {
      updatePolicyMutation.mutate({ id: policy.id, data: formData });
    } else {
      createPolicyMutation.mutate(formData);
    }
  };

  const isBridging = formData.interim_for_capability_development;

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-policydialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{policy?.id ? 'Edit Policy' : 'Design New Policy'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Policy Title *</Label>
            <Input data-testid="issues-policy-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Policy Statement (Clear & Unambiguous) *</Label>
            <Textarea data-testid="issues-policy-statement-textarea"
              value={formData.statement}
              onChange={(e) => setFormData({ ...formData, statement: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={3}
              placeholder="State the policy clearly and unambiguously..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Rationale</Label>
            <Textarea data-testid="issues-policy-rationale-textarea"
              value={formData.rationale}
              onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={2}
              placeholder="Why does this policy exist?"
            />
          </div>

          <div className="space-y-2">
            <Label>Restrictions (Painful but Necessary)</Label>
            <Textarea data-testid="issues-policy-restrictions-textarea"
              value={formData.restrictions}
              onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={2}
              placeholder="What restrictions does this policy impose?"
            />
          </div>

          <div className="space-y-2">
            <Label>Enforcement Mechanism</Label>
            <Textarea data-testid="issues-policy-enforcement-textarea"
              value={formData.enforcement_mechanism}
              onChange={(e) => setFormData({ ...formData, enforcement_mechanism: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={2}
              placeholder="How will this policy be enforced?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Relevance Scope</Label>
              <Input data-testid="issues-policy-scope-input"
                value={formData.relevance_scope}
                onChange={(e) => setFormData({ ...formData, relevance_scope: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                placeholder="Who/where does this apply?"
              />
            </div>

            <div className="space-y-2">
              <Label>Time Bound</Label>
              <Input data-testid="issues-policy-timebound-input"
                value={formData.time_bound}
                onChange={(e) => setFormData({ ...formData, time_bound: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                placeholder="e.g., Review quarterly"
              />
            </div>
          </div>

          {/* New Fields: Review Date & Sunset Trigger */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Mandatory Review Date
                {isBridging && !formData.review_date && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-none text-[10px]">REQUIRED</Badge>
                )}
              </Label>
              <Input data-testid="issues-policy-date-input"
                type="date"
                value={formData.review_date}
                onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                className={`bg-white text-slate-900 ${isBridging ? 'border-amber-500' : 'border-slate-300'}`}
                required={isBridging}
              />
              {isBridging && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Bridging policies MUST have a review date. Policies without expiration become organizational calcification.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Enforceability</Label>
              <Select value={formData.enforceability_score} onValueChange={(v) => setFormData({ ...formData, enforceability_score: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue placeholder="How enforceable?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="difficult">Difficult</SelectItem>
                  <SelectItem value="unenforceable">Unenforceable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sunset Trigger — What condition retires this policy?</Label>
            <Textarea data-testid="issues-policy-sunset-textarea"
              value={formData.sunset_trigger}
              onChange={(e) => setFormData({ ...formData, sunset_trigger: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={2}
              placeholder='e.g., "When the new CRM capability is operational"'
            />
          </div>

          <div className="space-y-2">
            <Label>Enforceability Notes</Label>
            <Textarea data-testid="issues-policy-success-textarea"
              value={formData.enforceability_notes}
              onChange={(e) => setFormData({ ...formData, enforceability_notes: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={2}
              placeholder="Notes on enforcement mechanism and challenges..."
            />
          </div>

          {/* Linked Capabilities (Bridging For) */}
          <div className="space-y-2">
            <Label>Bridging For (Capabilities Under Development)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-slate-950 border-slate-800">
                  {formData.linked_capability_ids.length === 0 ? 'Select capabilities...' : `${formData.linked_capability_ids.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-slate-900 border-slate-800">
                <Command>
                  <CommandInput placeholder="Search capabilities..." />
                  <CommandList>
                    <CommandEmpty>No capabilities found.</CommandEmpty>
                    <CommandGroup>
                      {(capabilities || []).map((cap) => (
                        <CommandItem
                          key={cap.id}
                          onSelect={() => {
                            setFormData({
                              ...formData,
                              linked_capability_ids: formData.linked_capability_ids.includes(cap.id)
                                ? formData.linked_capability_ids.filter((id) => id !== cap.id)
                                : [...formData.linked_capability_ids, cap.id]
                            });
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-4 h-4 border rounded ${formData.linked_capability_ids.includes(cap.id) ? 'bg-emerald-600 border-emerald-600' : 'border-slate-600'}`} />
                            <span className="flex-1">{cap.name}</span>
                            <Badge className="text-[10px] bg-slate-700">{cap.status}</Badge>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.linked_capability_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.linked_capability_ids.map((capId) => {
                  const cap = (capabilities || []).find((c) => c.id === capId);
                  return (
                    <Badge key={capId} variant="secondary" className="bg-emerald-500/10 text-emerald-400">
                      {cap?.name || capId}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          linked_capability_ids: formData.linked_capability_ids.filter((id) => id !== capId)
                        })}
                        className="ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Linked Issues */}
          <div className="space-y-2">
            <Label>Linked Issues</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-slate-950 border-slate-800">
                  {formData.linked_issue_ids.length === 0 ? 'Select issues...' : `${formData.linked_issue_ids.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-slate-900 border-slate-800">
                <Command>
                  <CommandInput placeholder="Search issues..." />
                  <CommandList>
                    <CommandEmpty>No issues found.</CommandEmpty>
                    <CommandGroup>
                      {(issues || []).map((issue) => (
                        <CommandItem
                          key={issue.id}
                          onSelect={() => {
                            setFormData({
                              ...formData,
                              linked_issue_ids: formData.linked_issue_ids.includes(issue.id)
                                ? formData.linked_issue_ids.filter((id) => id !== issue.id)
                                : [...formData.linked_issue_ids, issue.id]
                            });
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-4 h-4 border rounded ${formData.linked_issue_ids.includes(issue.id) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} />
                            <span className="flex-1">{issue.title}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.linked_issue_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.linked_issue_ids.map((issueId) => {
                  const issue = (issues || []).find((i) => i.id === issueId);
                  return (
                    <Badge key={issueId} variant="secondary" className="bg-red-500/10 text-red-400">
                      {issue?.title || issueId}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          linked_issue_ids: formData.linked_issue_ids.filter((id) => id !== issueId)
                        })}
                        className="ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input data-testid="issues-policy-review-date-input"
              type="checkbox"
              id="interim"
              checked={formData.interim_for_capability_development}
              onChange={(e) => setFormData({ ...formData, interim_for_capability_development: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="interim" className="cursor-pointer">
              This is an interim policy while capability is being developed
            </Label>
          </div>

          <div className="flex justify-end gap-3">
            <Button data-testid="issues-policy-cancel-btn" type="button" variant="outline" onClick={onClose} className="text-slate-900">
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {policy?.id ? 'Update Policy' : 'Create Policy'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}